// src/pages/PageEditPage.jsx
import React, { useEffect, useState, useCallback } from 'react'; // useCallback をインポート
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header';
import LoadingSpinner from '../components/LoadingSpinner';
import ChatGPTCodeGenerator from '../components/ChatGPTCodeGenerator';
import { createPage, getPageById, updatePage, getPages, getSettings } from '../services/firestoreService'; // getPages と getSettings をインポート
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { generateAndSavePageHTML } from '../services/htmlGeneratorService';
import { getSubdomainByAccountId } from '../services/subdomainService';
import { useAuth } from '../contexts/AuthContext';
import { useColorPalette } from '../contexts/ColorPaletteContext';
import { compileScss, validateScss, getScssError } from '../utils/scssCompiler';
import { getHeaderParts, getFooterParts } from '../services/commonPartsService';
import ImageSelector from '../components/ImageSelector';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-html';
import 'ace-builds/src-noconflict/mode-scss';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-tomorrow_night';
import 'ace-builds/src-noconflict/ext-language_tools';
import '../styles/pages/PageEditPage.scss';

// 一時保存データのキー
const DRAFT_STORAGE_KEY = 'suisui_page_draft';

const PageEditPage = () => {
    const { pageId } = useParams();
    const navigate = useNavigate();
    const { currentUser, accountId, loading: authLoading } = useAuth();
    const { availablePalettes, defaultPalette, customPalettes } = useColorPalette();

    const [page, setPage] = useState({
        title: '',
        slug: '',
        html: '', // 完全なHTMLファイル
        status: 'draft',
        snsImage: '',
        parentId: '', // <-- ここを追加: parentId を初期化
        colorPalette: '', // ページ固有のカラーパレット（空の場合はデフォルト使用）
        disableColorPalette: false, // カラーパレットを完全に無効にするかどうか
        headerPartId: '', // ヘッダーパーツID
        footerPartId: '', // フッターパーツID
        customHead: '', // カスタムhead内容
        customBody: '', // カスタムbody内容
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isNewPage, setIsNewPage] = useState(true);
    const [allPages, setAllPages] = useState([]); // <-- ここを追加: 全ページを格納するstate
    const [activeTab, setActiveTab] = useState('html'); // 'html' のみ
    const [hasDraft, setHasDraft] = useState(false);
    const [lastSavedTime, setLastSavedTime] = useState(null);
    const [isChatGPTOpen, setIsChatGPTOpen] = useState(false); // ChatGPTモーダルの状態
    const [originalSlug, setOriginalSlug] = useState(''); // slug変更検出用
    const [showSnsImageSelector, setShowSnsImageSelector] = useState(false); // SNS画像選択モーダルの状態
    const [headerParts, setHeaderParts] = useState([]);
    const [footerParts, setFooterParts] = useState([]);


    // ヘッダー・フッターパーツを取得する関数
    const fetchCommonParts = useCallback(async () => {
        if (!currentUser) return;
        try {
            const [headers, footers] = await Promise.all([
                getHeaderParts(currentUser.uid),
                getFooterParts(currentUser.uid)
            ]);
            setHeaderParts(headers);
            setFooterParts(footers);
        } catch (err) {
            console.error('共通パーツの取得に失敗しました:', err);
        }
    }, [currentUser]);

    // 親ページ選択肢のための全ページ取得関数
    const fetchAllPages = useCallback(async () => {
        if (!currentUser) return; // 認証情報がなければスキップ
        try {
            const result = await getPages(currentUser.uid);
            // 自分自身（編集中のページ）を親の選択肢から除外する
            const filteredPages = result.pages.filter(p => p.id !== pageId);
            setAllPages(filteredPages);
        } catch (err) {
            console.error('全ページの取得に失敗しました:', err);
        }
    }, [currentUser, pageId]);

    // 同じ親ページ配下でslugの重複をチェックする関数
    const checkSlugDuplicate = useCallback((slug, parentId, excludePageId = null) => {
        if (!slug) return false;

        const targetParentId = parentId === '' ? null : parentId;

        return allPages.some(page => {
            // 除外するページIDがある場合はスキップ
            if (excludePageId && page.id === excludePageId) {
                return false;
            }

            // 同じ親ページ配下で同じslugが存在するかチェック
            const pageParentId = page.parentId === '' ? null : page.parentId;
            return page.slug === slug && pageParentId === targetParentId;
        });
    }, [allPages]);


    // ChatGPTから生成されたコードを適用する関数
    const handleChatGPTCodeGenerated = useCallback((generatedCode) => {
        if (generatedCode.fullHTML) {
            let htmlContent = generatedCode.fullHTML;

            // カラーパレットが無効でない場合のみCSS読み込みリンクを追加
            if (!page.disableColorPalette) {
                const paletteToUse = page.colorPalette || defaultPalette || 'default';
                let cssUrl = `/api/generate-palette-css?palette=${paletteToUse}`;

                // カスタムパレットがある場合はURLパラメータに追加
                if (customPalettes && Object.keys(customPalettes).length > 0) {
                    const customPalettesParam = encodeURIComponent(JSON.stringify(customPalettes));
                    cssUrl += `&customPalettes=${customPalettesParam}`;
                }

                const cssLinkTag = `<link rel="stylesheet" href="${cssUrl}" id="color-palette-css">`;

                // <head>タグ内にCSS読み込みリンクを挿入
                if (htmlContent.includes('<head>')) {
                    htmlContent = htmlContent.replace(
                        '</head>',
                        `  ${cssLinkTag}\n</head>`
                    );
                } else if (htmlContent.includes('<!DOCTYPE')) {
                    // <head>タグがない場合は作成して挿入
                    htmlContent = htmlContent.replace(
                        '<html',
                        `<html`
                    ).replace(
                        /<html[^>]*>/,
                        `$&\n<head>\n  ${cssLinkTag}\n</head>`
                    );
                }
            }

            setPage(prevPage => ({
                ...prevPage,
                html: htmlContent
            }));

            // 成功メッセージを表示
            const message = page.disableColorPalette
                ? 'ChatGPTから生成されたHTMLが適用されました！'
                : 'ChatGPTから生成されたHTMLが適用されました！カラーパレットも自動的に適用されます。';
            alert(message);
        }
    }, [page.colorPalette, page.disableColorPalette, defaultPalette, customPalettes]);

    // HTMLエディターの変更処理
    const handleEditorChange = useCallback((value) => {
        setPage(prev => ({ ...prev, html: value }));
    }, []);

    // SNS画像選択のハンドラー
    const handleSnsImageSelect = useCallback((url) => {
        setPage(prev => ({
            ...prev,
            snsImage: url
        }));
    }, []);

    // 一時保存データを読み込む
    const loadDraft = useCallback(() => {
        const draftData = localStorage.getItem(DRAFT_STORAGE_KEY);
        if (draftData) {
            const draft = JSON.parse(draftData);
            if (draft.pageId === pageId) {
                setPage(draft.data);
                setLastSavedTime(new Date(draft.timestamp));
                setHasDraft(true);
                return true;
            }
        }
        return false;
    }, [pageId]);

    // 一時保存データを保存
    const saveDraft = useCallback(() => {
        const draftData = {
            pageId,
            data: page,
            timestamp: new Date().toISOString()
        };
        localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draftData));
        setLastSavedTime(new Date());
        setHasDraft(true);
    }, [page, pageId]);

    // 一時保存データを削除
    const clearDraft = useCallback(() => {
        localStorage.removeItem(DRAFT_STORAGE_KEY);
        setHasDraft(false);
        setLastSavedTime(null);
    }, []);

    useEffect(() => {
        if (authLoading) return;

        if (!currentUser) {
            navigate('/login');
            return;
        }

        // 全ページを取得
        fetchAllPages();

        // 共通パーツを取得
        fetchCommonParts();

        if (pageId) {
            setIsNewPage(false);
            const fetchPage = async () => {
                setLoading(true);
                setError(null);
                try {
                    // 一時保存データがあるか確認
                    const hasDraftData = loadDraft();

                    if (!hasDraftData) {
                        const fetchedPage = await getPageById(pageId);
                        if (fetchedPage) {
                            // slug変更検出のため、現在のslugを保存
                            setOriginalSlug(fetchedPage.slug || '');

                            // 既存のcontentをheadとbodyに分割
                            const content = fetchedPage.content || '';
                            const headMatch = content.match(/<head>([\s\S]*?)<\/head>/);
                            const bodyMatch = content.match(/<body>([\s\S]*?)<\/body>/);

                            let extractedHead = headMatch ? headMatch[1].trim() : '';
                            const extractedBody = bodyMatch ? bodyMatch[1].trim() : content;

                            // CSSがheadに含まれている場合は除外
                            if (fetchedPage.css && extractedHead.includes(`<style>${fetchedPage.css}</style>`)) {
                                extractedHead = extractedHead.replace(`<style>${fetchedPage.css}</style>`, '').trim();
                            }

                            setPage({
                                ...fetchedPage,
                                html: content, // 完全なHTMLを保存
                                css: fetchedPage.css || '',
                                jsonld: fetchedPage.jsonld || '',
                                snsImage: fetchedPage.snsImage || '',
                                disableColorPalette: fetchedPage.disableColorPalette || false,
                                parentId: fetchedPage.parentId || '',
                            });
                        } else {
                            // ページが見つからない場合
                            console.error('PageEditPage: 指定されたページが見つかりませんでした。ID:', pageId); // <-- 追加
                            setError('指定されたページが見つかりませんでした。');
                            navigate(`/${accountId}/pages`); // 一覧ページに戻る
                        }
                    }
                } catch (err) {
                    // データ取得中にエラーが発生した場合
                    console.error('PageEditPage: ページの取得に失敗しました:', err); // <-- 詳細なエラーログを追加
                    setError('ページの読み込み中にエラーが発生しました。');
                    navigate(`/${accountId}/pages`); // <-- エラー発生時も一覧ページに戻る
                } finally {
                    setLoading(false);
                }
            };
            fetchPage();
        } else {
            setIsNewPage(true);
            setPage({
                title: '',
                slug: '',
                html: `<!-- セキュリティヘッダー -->
<meta http-equiv="Content-Security-Policy" content="default-src 'self'; script-src 'self' 'unsafe-inline' 'unsafe-eval'; style-src 'self' 'unsafe-inline'; img-src 'self' data: https:; font-src 'self' data:;">
<meta http-equiv="X-Frame-Options" content="DENY">
<meta http-equiv="X-Content-Type-Options" content="nosniff">
<meta name="referrer" content="strict-origin-when-cross-origin">
<meta http-equiv="Permissions-Policy" content="camera=(), microphone=(), geolocation=()">

<!-- 基本的なメタタグ -->
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<meta name="description" content="ページの説明を入力してください">
<meta name="keywords" content="キーワード1, キーワード2">
<meta name="author" content="著者名">

<!-- Open Graph / Facebook -->
<meta property="og:type" content="website">
<meta property="og:url" content="">
<meta property="og:title" content="">
<meta property="og:description" content="">
<meta property="og:image" content="${page.snsImage || ''}">

<!-- Twitter -->
<meta property="twitter:card" content="summary_large_image">
<meta property="twitter:url" content="">
<meta property="twitter:title" content="">
<meta property="twitter:description" content="">
<meta property="twitter:image" content="${page.snsImage || ''}">

<!-- ファビコン -->
<link rel="icon" type="image/x-icon" href="/favicon.ico">

<!-- スタイルシート -->
<link rel="stylesheet" href="/css/style.css">

<!-- Page Custom Head -->
${page.customHead ? page.customHead : ''}`,
                css: '',
                status: 'draft',
                jsonld: '',
                snsImage: '',
                parentId: '',
            });
            loadDraft(); // 新規作成時も一時保存データを確認
        }
    }, [pageId, currentUser, authLoading, navigate, fetchAllPages, loadDraft, accountId]);


    const handleChange = (e) => {
        const { name, value } = e.target;
        setPage((prevPage) => ({
            ...prevPage,
            [name]: value,
        }));

        // slugが変更された場合、リアルタイムで重複チェック
        if (name === 'slug' && value) {
            const isDuplicate = checkSlugDuplicate(value, page.parentId, isNewPage ? null : pageId);
            if (isDuplicate) {
                setError('同じ親ページ配下に同じスラッグが既に存在します。別のスラッグを入力してください。');
            } else {
                setError(null);
            }
        }

        // 親ページが変更された場合、現在のslugで重複チェック
        if (name === 'parentId' && page.slug) {
            const isDuplicate = checkSlugDuplicate(page.slug, value, isNewPage ? null : pageId);
            if (isDuplicate) {
                setError('同じ親ページ配下に同じスラッグが既に存在します。別のスラッグを入力してください。');
            } else {
                setError(null);
            }
        }
    };

    // SCSSのリアルタイムバリデーション
    const validateScssInRealTime = async (scssCode) => {
        try {
            const error = await getScssError(scssCode);
            setError(error);
        } catch (err) {
            console.error('SCSSバリデーションエラー:', err);
        }
    };

    // Ace Editorの共通設定
    const getAceEditorProps = (mode) => ({
        mode: mode,
        theme: 'tomorrow_night',
        fontSize: 14,
        width: '100%',
        height: '400px',
        setOptions: {
            enableBasicAutocompletion: true,
            enableLiveAutocompletion: true,
            enableSnippets: false,
            showLineNumbers: true,
            tabSize: 2,
            useWorker: false
        }
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        if (!currentUser) {
            setError('ログインしていません。');
            setLoading(false);
            return;
        }

        // slugの重複チェック
        const isDuplicate = checkSlugDuplicate(page.slug, page.parentId, isNewPage ? null : pageId);
        if (isDuplicate) {
            setError('同じ親ページ配下に同じスラッグが既に存在します。別のスラッグを入力してください。');
            setLoading(false);
            return;
        }

        // SCSSエラーチェック
        if (page.css && error) {
            setError(`SCSSエラー: ${error}`);
            setLoading(false);
            return;
        }

        try {
            // HTMLからbodyコンテンツを抽出
            let bodyContent = '';
            let headContent = '';

            if (page.html) {
                const bodyMatch = page.html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
                if (bodyMatch) {
                    bodyContent = bodyMatch[1].trim();
                }

                const headMatch = page.html.match(/<head[^>]*>([\s\S]*?)<\/head>/i);
                if (headMatch) {
                    headContent = headMatch[1].trim();
                }
            }

            // 最終的なcontentを作成
            const content = page.html; // 完全なHTMLファイルをそのまま保存

            // 保存するデータを準備
            const pageToSave = {
                ...page,
                content,
                body: bodyContent, // HTMLから抽出したbodyコンテンツを明示的に保存
                head: headContent, // HTMLから抽出したheadコンテンツを明示的に保存
                updatedAt: new Date()
            };


            let savedPageId = pageId;
            if (isNewPage) {
                const result = await createPage(pageToSave, currentUser.uid);
                savedPageId = result.id;
                alert('ページが作成されました！');
            } else {
                await updatePage(pageId, pageToSave);
                alert('ページが更新されました！');
            }

            // ページが公開状態の場合、HTMLファイルを生成

            if (page.status === 'published' && page.slug) {
                try {

                    // slug変更時の旧HTMLファイル削除
                    if (!isNewPage && originalSlug && originalSlug !== page.slug) {

                        try {
                            const { deleteStaticPageHTML } = await import('../services/staticHostingService');

                            // メインサイト用の旧HTMLファイルを削除
                            await deleteStaticPageHTML(originalSlug);

                            // サブドメイン用の旧HTMLファイルを削除
                            if (subdomainData && subdomainData.subdomain) {
                                await deleteStaticPageHTML(originalSlug, subdomainData.subdomain);
                            }
                        } catch (deleteError) {
                            // 削除エラーは処理を停止させない
                        }
                    }

                    alert(`HTMLファイル生成を開始します: ${page.slug}`);

                    // 保存されたデータを使ってHTMLファイルを生成（pageIdからの再取得は避ける）
                    const savedPageData = { ...pageToSave, id: savedPageId };

                    // メインサイト用のHTMLファイルを生成
                    const { generateAndSavePageHTMLWithData } = await import('../services/htmlGeneratorService');
                    await generateAndSavePageHTMLWithData(savedPageData, page.slug, null, currentUser.uid);

                    // サブドメイン用のHTMLファイルを生成
                    try {
                        const subdomainData = await getSubdomainByAccountId(currentUser.uid);

                        if (subdomainData) {
                            await generateAndSavePageHTMLWithData(savedPageData, page.slug, subdomainData.subdomain, currentUser.uid);
                            alert(`サブドメイン ${subdomainData.subdomain} 用HTMLファイル生成完了！`);
                        } else {
                            alert('サブドメインが登録されていません');
                        }
                    } catch (subdomainError) {
                        console.error('⚠️ サブドメイン用HTMLファイル生成エラー:', subdomainError);
                        // サブドメイン用の生成に失敗してもメイン保存は成功とする
                    }

                } catch (htmlError) {
                    console.error('⚠️ HTMLファイル生成エラー:', htmlError);
                    // HTMLファイル生成に失敗してもページ保存は成功とする
                }
            }

            clearDraft(); // 公開後は一時保存データを削除
            navigate(`/pages/${accountId}`);
        } catch (err) {
            console.error('ページの保存に失敗しました:', err);
            setError(`ページの保存中にエラーが発生しました: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // JSON-LD生成関数
    const generateJsonld = async () => {
        setIsGeneratingJsonld(true);
        try {
            // コンテンツからタイトルと説明を抽出
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = page.html;

            // タイトルを抽出（h1タグまたは最初のh2タグ）
            const title = tempDiv.querySelector('h1')?.textContent ||
                tempDiv.querySelector('h2')?.textContent ||
                page.title;

            // 説明を抽出（最初のpタグ）
            const description = tempDiv.querySelector('p')?.textContent || '';

            // 画像URLを抽出（最初のimgタグ）
            const imageUrl = tempDiv.querySelector('img')?.src || page.snsImage || '';

            // JSON-LDを生成
            const jsonld = {
                "@context": "https://schema.org",
                "@type": "WebPage",
                "name": title,
                "description": description,
                "url": `${window.location.origin}/${page.slug}`,
                "image": imageUrl,
                "datePublished": new Date().toISOString(),
                "dateModified": new Date().toISOString(),
                "author": {
                    "@type": "Organization",
                    "name": "OUR COMPANY"
                }
            };

            // コンテンツの種類に応じて追加の構造化データを生成
            if (page.html.includes('news-item')) {
                // ニュース記事の場合
                jsonld["@type"] = "NewsArticle";
                const newsItems = tempDiv.querySelectorAll('.news-item');
                if (newsItems.length > 0) {
                    const latestNews = newsItems[0];
                    const date = latestNews.querySelector('.news-date')?.textContent;
                    if (date) {
                        jsonld["datePublished"] = new Date(date).toISOString();
                    }
                }
            } else if (page.html.includes('service-item')) {
                // サービスページの場合
                jsonld["@type"] = "Service";
                const services = Array.from(tempDiv.querySelectorAll('.service-item')).map(item => ({
                    "@type": "Service",
                    "name": item.querySelector('h3')?.textContent,
                    "description": item.querySelector('p')?.textContent
                }));
                jsonld["hasOfferCatalog"] = {
                    "@type": "OfferCatalog",
                    "itemListElement": services
                };
            }

            // JSON-LDを更新
            setPage(prev => ({
                ...prev,
                jsonld: JSON.stringify(jsonld, null, 2)
            }));

        } catch (error) {
            console.error('JSON-LD生成エラー:', error);
            alert('JSON-LDの生成中にエラーが発生しました。');
        } finally {
            setIsGeneratingJsonld(false);
        }
    };



    if (authLoading || loading) {
        return <LoadingSpinner />;
    }

    return (
        <div>
            <Header />
            <main className="page-edit-container">
                {hasDraft && lastSavedTime && (
                    <div className="draft-info">
                        <p>
                            一時保存データがあります（最終保存: {lastSavedTime.toLocaleString()}）
                            <button
                                type="button"
                                className="clear-draft-button"
                                onClick={clearDraft}
                            >
                                一時保存を破棄
                            </button>
                        </p>
                    </div>
                )}
                <form onSubmit={handleSubmit} className="page-form">
                    {/* 基本情報を3列で配置 */}
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="title">タイトル *</label>
                            <input
                                type="text"
                                id="title"
                                name="title"
                                value={page.title}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="slug">スラッグ *</label>
                            <input
                                type="text"
                                id="slug"
                                name="slug"
                                value={page.slug}
                                onChange={handleChange}
                                required
                                className={error && error.includes('スラッグ') ? 'error-input' : ''}
                            />
                            <small>URLの一部になります (例: about-us)</small>
                            {error && error.includes('スラッグ') && (
                                <small className="error-text">⚠️ {error}</small>
                            )}
                        </div>
                        <div className="form-group">
                            <label htmlFor="parentId">親ページ</label>
                            <select
                                id="parentId"
                                name="parentId"
                                value={page.parentId || ''} // parentId が null の場合は空文字列に変換
                                onChange={handleChange}
                            >
                                <option value="">なし (ルートページ)</option> {/* ルートページ選択肢 */}
                                {allPages.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.title}
                                    </option>
                                ))}
                            </select>
                            <small>このページの親となるページを選択します。</small>
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="status">ページのステータス</label>
                            <select
                                id="status"
                                name="status"
                                value={page.status}
                                onChange={handleChange}
                            >
                                <option value="draft">下書き</option>
                                <option value="published">公開</option>
                                <option value="unpublished">非公開</option>
                                <option value="archived">アーカイブ（ゴミ箱）</option>
                            </select>
                            {/* <small>
                                下書き：作成中の状態です。<br />
                                公開：ページが公開され、誰でも閲覧できます。<br />
                                非公開：一時的に公開を停止します。<br />
                                アーカイブ：過去の記録として保存します。
                            </small> */}
                        </div>
                        <div className="form-group">
                            <label htmlFor="headerPartId">ヘッダーパーツ</label>
                            <select
                                id="headerPartId"
                                name="headerPartId"
                                value={page.headerPartId || ''}
                                onChange={handleChange}
                            >
                                <option value="">なし（カスタム）</option>
                                {headerParts.map(part => (
                                    <option key={part.id} value={part.id}>
                                        {part.name}
                                    </option>
                                ))}
                            </select>
                            <small>使用するヘッダーパーツを選択します</small>
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="footerPartId">フッターパーツ</label>
                            <select
                                id="footerPartId"
                                name="footerPartId"
                                value={page.footerPartId || ''}
                                onChange={handleChange}
                            >
                                <option value="">なし（カスタム）</option>
                                {footerParts.map(part => (
                                    <option key={part.id} value={part.id}>
                                        {part.name}
                                    </option>
                                ))}
                            </select>
                            <small>使用するフッターパーツを選択します</small>
                        </div>
                        <div className="form-group">
                            {/* 空のスペースを埋めるための空のdiv */}
                        </div>
                        <div className="form-group">
                            {/* 空のスペースを埋めるための空のdiv */}
                        </div>
                    </div>
                    <div className="form-group">
                        <label htmlFor="snsImage">SNS画像URL</label>
                        <div className="input-group">
                            <input
                                type="text"
                                id="snsImage"
                                name="snsImage"
                                value={page.snsImage}
                                onChange={handleChange}
                                placeholder="https://example.com/sns_image.jpg"
                            />
                            <button
                                type="button"
                                onClick={() => setShowSnsImageSelector(true)}
                                className="select-button"
                            >
                                選択
                            </button>
                        </div>
                        {page.snsImage && (
                            <div className="image-preview">
                                <p>プレビュー:</p>
                                <img
                                    src={page.snsImage}
                                    alt="SNS Image Preview"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'block';
                                    }}
                                />
                                <span className="image-error" style={{ display: 'none' }}>
                                    画像の読み込みに失敗しました
                                </span>
                            </div>
                        )}
                        <small>SNS共有時に使用する画像のURL。設定されていない場合は、グローバル設定のSNSシェア画像が使用されます。</small>
                    </div>

                    {/* カスタムhead&body設定 */}
                    <div className="form-group">
                        <label htmlFor="customHead">カスタムhead内容</label>
                        <textarea
                            id="customHead"
                            name="customHead"
                            value={page.customHead}
                            onChange={handleChange}
                            rows={8}
                            className="textarea-large"
                        />
                        <small>
                            このページのhead要素に挿入されるHTMLコードを入力してください。
                            headタグは不要です。Google Analytics、Google Tag Manager、その他のトラッキングコードやメタタグを含めることができます。
                        </small>
                    </div>

                    <div className="form-group">
                        <label htmlFor="customBody">カスタムbody内容</label>
                        <textarea
                            id="customBody"
                            name="customBody"
                            value={page.customBody}
                            onChange={handleChange}
                            rows={8}
                            className="textarea-large"
                        />
                        <small>
                            このページのbody要素の開始直後に挿入されるHTMLコードを入力してください。
                            bodyタグは不要です。Google Tag Manager (noscript)、チャットボット、その他のスクリプトやウィジェットを含めることができます。
                        </small>
                    </div>

                    {/* カラーパレット設定 */}
                    <div className="form-group">
                        <label>カラーパレット設定</label>

                        <div className="palette-settings">
                            <div className="palette-selection">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        name="disableColorPalette"
                                        checked={page.disableColorPalette}
                                        onChange={(e) => setPage(prev => ({
                                            ...prev,
                                            disableColorPalette: e.target.checked,
                                            colorPalette: e.target.checked ? '' : prev.colorPalette
                                        }))}
                                    />
                                    カラーパレットを利用しない
                                </label>
                                <small>
                                    有効にすると、このページではカラーパレットを使用せず、直接色指定のみが可能になります。
                                </small>

                                {!page.disableColorPalette && (
                                    <div className="palette-mode-selection">
                                        <label>カラーパレット設定</label>

                                        <div className="palette-mode-options">
                                            <div className="palette-option">
                                                <label className={`radio-label ${!page.colorPalette ? 'selected' : ''}`}>
                                                    <input
                                                        type="radio"
                                                        name="paletteMode"
                                                        checked={!page.colorPalette}
                                                        onChange={() => setPage(prev => ({
                                                            ...prev,
                                                            colorPalette: ''
                                                        }))}
                                                    />
                                                    <span className="option-text">デフォルトパレットを使用</span>
                                                </label>
                                                <small>
                                                    デフォルトパレット「{availablePalettes?.find(p => p.key === defaultPalette)?.name || 'デフォルト'}」を使用し、AI生成時も自動的にこのパレットが適用されます。
                                                </small>
                                            </div>

                                            <div className="palette-option">
                                                <label className={`radio-label ${!!page.colorPalette ? 'selected' : ''}`}>
                                                    <input
                                                        type="radio"
                                                        name="paletteMode"
                                                        checked={!!page.colorPalette}
                                                        onChange={() => {
                                                            // 特定パレットモードに切り替え時、最初のパレットを選択
                                                            if (!page.colorPalette && availablePalettes && availablePalettes.length > 0) {
                                                                setPage(prev => ({
                                                                    ...prev,
                                                                    colorPalette: availablePalettes[0].key
                                                                }));
                                                            }
                                                        }}
                                                    />
                                                    <span className="option-text">特定のパレットを指定</span>
                                                </label>
                                                <small>
                                                    特定のパレットを選択し、AI生成時は利用可能なパレットから選択できます。
                                                </small>
                                            </div>

                                            {page.colorPalette && (
                                                <div className="specific-palette-selection">
                                                    <label>使用するカラーパレット</label>
                                                    <div className="palette-grid">
                                                        {(availablePalettes || []).map((palette) => (
                                                            <div
                                                                key={palette.key}
                                                                className={`palette-card ${page.colorPalette === palette.key ? 'selected' : ''}`}
                                                                onClick={() => setPage(prev => ({
                                                                    ...prev,
                                                                    colorPalette: palette.key
                                                                }))}
                                                            >
                                                                <div className="palette-preview">
                                                                    {palette.colors && Object.entries(palette.colors).slice(0, 4).map(([colorKey, colorValue]) => (
                                                                        <div
                                                                            key={colorKey}
                                                                            className="color-swatch"
                                                                            style={{ backgroundColor: colorValue }}
                                                                            title={`${colorKey}: ${colorValue}`}
                                                                        />
                                                                    ))}
                                                                </div>
                                                                <div className="palette-info">
                                                                    <div className="palette-name">
                                                                        {palette.name}
                                                                        {palette.key === defaultPalette && <span className="default-badge">デフォルト</span>}
                                                                        {palette.isCustom && <span className="custom-badge">カスタム</span>}
                                                                    </div>
                                                                    <div className="palette-key">{palette.key}</div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>コンテンツ</label>

                        {/* タブ形式（iPad用） */}
                        <div className="editor-tabs mobile-only">
                            <button
                                type="button"
                                className={`tab-button ${activeTab === 'html' ? 'active' : ''}`}
                                onClick={() => setActiveTab('html')}
                            >
                                HTML
                            </button>

                            {/* ChatGPTコード生成ボタン */}
                            <button
                                type="button"
                                className="chatgpt-button"
                                onClick={() => setIsChatGPTOpen(true)}
                                title="ChatGPTと相談してコードを生成"
                            >
                                🤖 AI生成
                            </button>
                        </div>

                        {/* 横並びエディタ（PC用） */}
                        <div className="editor-grid desktop-only">
                            {/* ChatGPTコード生成ボタン */}
                            <div className="chatgpt-section">
                                <button
                                    type="button"
                                    className="chatgpt-button-large"
                                    onClick={() => setIsChatGPTOpen(true)}
                                    title="ChatGPTと相談してコードを生成"
                                >
                                    🤖 ChatGPTと相談してコード生成
                                </button>
                                <small>AIと相談して、理想のWebサイトのHTMLを自動生成しましょう</small>
                            </div>

                            <div className="editor-section">
                                <h4>HTML (完全なHTMLファイル)</h4>
                                {!loading && (
                                    <AceEditor
                                        {...getAceEditorProps('html')}
                                        value={page.html}
                                        onChange={handleEditorChange}
                                        name="html-editor"
                                        height="600px"
                                    />
                                )}
                                <small>完全なHTMLファイルを記述してください。DOCTYPE宣言、html、head、bodyタグを含む完全な構造を記述できます。</small>
                            </div>
                        </div>

                        {/* タブエディタ（iPad用） */}
                        <div className="tab-editor mobile-only">
                            {!loading && (
                                <AceEditor
                                    {...getAceEditorProps('html')}
                                    value={page.html}
                                    onChange={handleEditorChange}
                                    name="html-editor"
                                    height="500px"
                                />
                            )}
                            <small>完全なHTMLファイルを記述してください。DOCTYPE宣言、html、head、bodyタグを含む完全な構造を記述できます。</small>
                        </div>
                    </div>
                    {error && <p className="error-message">{error}</p>}
                    <div className="form-actions">
                        <button
                            type="button"
                            onClick={saveDraft}
                            className="draft-button"
                        >
                            一時保存
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="update-button"
                        >
                            {loading ? '更新中...' : isNewPage ? '作成' : '更新'}
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate(`/pages/${accountId}`)}
                            className="cancel-button"
                        >
                            キャンセル
                        </button>
                    </div>
                </form>
            </main>

            {/* ChatGPTコード生成モーダル */}
            <ChatGPTCodeGenerator
                isOpen={isChatGPTOpen}
                onClose={() => setIsChatGPTOpen(false)}
                onCodeGenerated={handleChatGPTCodeGenerated}
                defaultPalette={defaultPalette}
                availablePalettes={availablePalettes}
                pageSettings={{
                    disableColorPalette: page.disableColorPalette,
                    restrictPalettes: page.restrictPalettes,
                    allowedPalettes: page.allowedPalettes
                }}
            />

            {/* SNS画像選択モーダル */}
            <ImageSelector
                isOpen={showSnsImageSelector}
                onClose={() => setShowSnsImageSelector(false)}
                onSelect={handleSnsImageSelect}
                currentValue={page.snsImage}
            />
        </div>
    );
};

export default PageEditPage;